import React, { useEffect } from "react";
import { VideoView, useVideoPlayer } from "expo-video";
import styled, { css } from "styled-components/native";
import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { CarouselItemProps, imageTypes, videoTypes } from "../types";
import ImageWithLoader from "./ImageWithLoader";
import { Dimensions, Platform, StyleSheet } from "react-native";
import RenderHTML from "react-native-render-html";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

export const CarouselItem: React.FC<CarouselItemProps> = ({
  mediaItem,
  index,
  videoPlayers,
  typography,
  theme,
  isPostOnboarding,
  onVideoLoad,
}) => {
  const screenHeight = Dimensions.get("window").height;
  const { deviceWidth: width } = useIsFoldable();

  // Create video player for native platforms
  const playerFunc = useVideoPlayer(
    videoTypes.includes(mediaItem.media_type) && Platform.OS !== "web"
      ? mediaItem.media
      : null,
    (player) => {
      player.loop = true;
      player.muted = true;
      player.play();
    }
  );

  // Store player reference
  useEffect(() => {
    if (videoTypes.includes(mediaItem.media_type) && Platform.OS !== "web") {
      videoPlayers.current[index] = playerFunc;
      onVideoLoad?.(index);
    } else {
      videoPlayers.current[index] = null;
    }

    return () => {
      videoPlayers.current[index] = null;
    };
  }, [playerFunc, index, mediaItem.media_type, videoPlayers]);

  const renderMedia = (item: any) => {
    if (imageTypes.includes(item.media_type)) {
      return (
        <ImageWithLoader
          theme={theme}
          item={item}
          // @ts-ignore
          screenHeight={screenHeight}
          isPostOnboarding={isPostOnboarding}
        />
      );
    } else if (videoTypes.includes(item.media_type)) {
      return Platform.OS === "web" ? (
        <video
          style={{
            width: "100%",
            height: "100%",
          }}
          src={item.media}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => onVideoLoad?.(index)}
        />
      ) : (
        <VideoView
          player={playerFunc}
          style={styles.video}
          contentFit="cover"
          nativeControls={false}
        />
      );
    }
  };

  return (
    <Overlay>
      <>
        <LogoView
          screenHeight={screenHeight}
          isPostOnboarding={isPostOnboarding}
        >
          {renderMedia(mediaItem)}
        </LogoView>
        {!theme.isLargeScreen && <Spacer size={SpaceType.Double} />}
        <TextContainer>
          <ChildContainer>
            <RenderHTML
              contentWidth={width}
              source={{
                html: `${mediaItem.title}`,
              }}
              tagsStyles={{
                body: {
                  ...typography["H1-B"],
                  color: theme["core-05"],
                },
              }}
            />
            <Spacer size={SpaceType.Small} />
            <RenderHTML
              contentWidth={width}
              source={{
                html: `${mediaItem.description}`,
              }}
              tagsStyles={{
                body: {
                  ...typography["Body-R"],
                  color: theme["core-06"],
                },
              }}
            />
          </ChildContainer>
        </TextContainer>
      </>
    </Overlay>
  );
};

const styles = StyleSheet.create({
  video: {
    width: "100%",
    height: "100%",
  },
});

const Overlay = styled.View`
  width: 100%;
  padding-bottom: ${SpaceType.Big}px;
  flex: 1;
  ${({ theme }: { theme: any }) => css`
    ${theme.isLargeScreen &&
    `
      flex-direction: column;
    `}

    ${theme.isTablet &&
    `
      transform: translateY(-${SpaceType.Double}px);
    `}

    ${!theme.isMobile &&
    `
      justify-content: center;
      align-items: center;
    `}
  `}
`;

const TextContainer = styled.View`
  padding-horizontal: ${({ theme }: { theme: any }) =>
    theme.isMobile ? "16px" : "0px"};
  ${({ theme }: { theme: any }) =>
    theme.isLargeScreen &&
    css`
      flex-shrink: 1;
      min-height: 0;
      padding-top: 40px;
    `}
`;

const ChildContainer = styled.View`
  width: 100%;
  max-width: ${({ theme }: { theme: any }) =>
    theme.isMobile ? "343px" : "100%"};
`;

const LogoView = styled.View<{
  screenHeight: number;
  isPostOnboarding: boolean;
}>`
  width: 100%;
  ${({ theme, isPostOnboarding }) =>
    theme.isLargeScreen &&
    (isPostOnboarding
      ? css`
          flex: 1;
          min-height: 0;
        `
      : css`
          transform: translateY(-${SpaceType.LargeDouble}px);
        `)}

  ${({ theme }: { theme: any }) =>
    (theme.isMobile || (theme.isTablet && Platform.OS === "web")) &&
    css`
      height: 60%;
    `}

${({ theme }: { theme: any }) =>
    theme.isTablet &&
    css`
      aspect-ratio: 1.35/1;
    `}
`;
